import {
  HeroStandard,
  HeroColorBlock,
  HeroStandardProps,
  HeroFeatures,
} from '@zapier/design-system';
import styled from '@emotion/styled';
import { HeroVariant } from '../../../global/types';
import {
  CtaGoalType,
  CtaNameType,
  EventName,
  useTrackEvent,
  useAvroTracking,
} from '../../../observability';
import { COLOR_KEYS } from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { innerText } from '../../../utils/innerText';
import { RichTextHTMLString } from '../types';

type Props = HeroStandardProps & {
  variant: HeroVariant.standard | HeroVariant.colorBlock | HeroVariant.features;
  ctaName: CtaNameType;
  ctaGoal: CtaGoalType;
  ctaDisclaimer?: RichTextHTMLString;
  description: RichTextHTMLString;
  openInNewTab: boolean;
};

export function HeroSection(props: Props) {
  const trackEvent = useTrackEvent();
  const { emitCTAClickEvent } = useAvroTracking();

  const handleCtaClick = () => {
    emitCTAClickEvent({
      text: innerText(props.ctaText),
      section: `hero_section_${props.variant}`,
      href: props.ctaHref!,
    });
    trackEvent(EventName.CALL_TO_ACTION, {
      path: window.location.pathname,
      from_path: document.referrer,
      cta_name: props.ctaName,
      cta_raw_text: props.ctaText,
      cta_goal: props.ctaGoal,
    });
  };

  const getDefaultCtaColor = (): HeroStandardProps['ctaColor'] => {
    if (
      props.backgroundColor === COLOR_KEYS.night ||
      props.backgroundColor === COLOR_KEYS.moss ||
      props.backgroundColor === COLOR_KEYS.ocean40 ||
      props.backgroundColor === COLOR_KEYS.sun60 ||
      props.backgroundColor === COLOR_KEYS.neutral800
    ) {
      return 'primary-dark';
    }

    if (props.backgroundColor === COLOR_KEYS.zapier || COLOR_KEYS.lava60) {
      return 'primary-alt';
    }

    return 'primary';
  };

  const heroProps: HeroStandardProps = {
    heading: props.heading,
    ctaText: props.ctaText,
    ctaHref: props.ctaHref,
    ctaColor: props.ctaColor || getDefaultCtaColor(),
    ctaDisclaimer: props.ctaDisclaimer && (
      <RichTextHtml
        html={props.ctaDisclaimer}
        isDarkBackground={props.backgroundColor !== 'white'}
      />
    ),
    description: (
      <RichTextHtml
        html={props.description}
        isDarkBackground={props.backgroundColor !== 'white'}
      />
    ),
    onCtaClick: handleCtaClick,
    ctaTarget: props.openInNewTab ? '_blank' : '_self',
  };

  const heroFactory: { [key in HeroVariant]: JSX.Element } = {
    [HeroVariant.standard]: (
      <HeroStandard
        {...heroProps}
        backgroundColor={props.backgroundColor}
        maxWidth={1280}
      >
        <MediaChildren>{props.children}</MediaChildren>
      </HeroStandard>
    ),
    [HeroVariant.colorBlock]: (
      <HeroColorBlock
        {...heroProps}
        // @ts-ignore -`neutral100` is not a supported value for `HeroColorBlock`
        backgroundColor={props.backgroundColor}
      >
        <MediaChildren>{props.children}</MediaChildren>
      </HeroColorBlock>
    ),
    [HeroVariant.features]: (
      <HeroFeatures
        {...heroProps}
        // @ts-ignore - `HeroFeatures` only accepts V5 colors
        backgroundColor={props.backgroundColor}
      >
        {props.children}
      </HeroFeatures>
    ),
  };

  return heroFactory[props.variant] || null;
}

export const MediaChildren = styled.div`
  width: 100%;
  max-width: calc(100vw - 40px);
`;
